import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Radio, Undo2, Plus, Minus, Play, ClipboardList, ListOrdered, BarChart3, Repeat, Target, ShieldAlert } from "lucide-react";
import EntityLogo from "@/components/EntityLogo";

const GROUPS = [
  { title: "Hits", items: [["single", "1B"], ["double", "2B"], ["triple", "3B"], ["home_run", "HR"]] },
  { title: "On Base", items: [["walk", "BB"], ["hbp", "HBP"], ["reached_on_error", "ROE"]] },
  { title: "Outs", items: [["strikeout", "K"], ["groundout", "GO"], ["flyout", "FO"], ["lineout", "LO"], ["popout", "PO"], ["fielders_choice", "FC"], ["double_play", "DP"], ["sac_fly", "SF"], ["sac_bunt", "SAC"]] },
];
const LABELS = { single: "Single", double: "Double", triple: "Triple", home_run: "Home Run", walk: "Walk", hbp: "Hit By Pitch", reached_on_error: "Reached On Error", strikeout: "Strikeout", groundout: "Groundout", flyout: "Flyout", lineout: "Lineout", popout: "Popout", fielders_choice: "Fielder's Choice", double_play: "Double Play", sac_fly: "Sac Fly", sac_bunt: "Sac Bunt" };
const PITCHES = [["ball", "Ball"], ["called_strike", "Called K"], ["swinging_strike", "Swing K"], ["foul", "Foul"], ["in_play", "In Play"]];
const TABS = ["Live", "Box Score", "Play-by-Play", "Statistics"];

export default function LiveScoring({ game, team, roster, canManage, onChange }) {
  const [sc, setSc] = useState(null);
  const [tab, setTab] = useState("Live");

  const load = useCallback(() => {
    api.get(`/live/games/${game.id}/scoring`).then(({ data }) => setSc(data)).catch(() => {});
  }, [game.id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { load(); }, [game.status, load]);

  if (!sc) return <div className="py-16 text-center text-sm text-slate-400">Loading game…</div>;

  const st = sc.state;
  const battingSide = st.half === "top" ? "away" : "home";
  const isOurBat = battingSide === sc.our_side;
  const home = sc.box_score.home, away = sc.box_score.away;
  const isFinal = sc.status === "final";
  const isAdvanced = sc.scoring_mode === "advanced";
  const count = sc.count || { balls: 0, strikes: 0 };

  return (
    <div data-testid="live-scoring">
      {/* Live header */}
      <div className="bg-[#0A192F] text-white" data-testid="live-header">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold bg-[#E63946] px-2 py-0.5 rounded-sm">{isFinal ? "FINAL" : "● LIVE"}</span>
            <span className="text-xs text-white/50 capitalize">{isAdvanced ? "Advanced" : "Basic"} · {game.game_type} · {game.location || ""}</span>
          </div>
          <div className="grid grid-cols-3 items-center mt-3 text-center">
            <ScoreCol name={home.team?.name || "Home"} logo={home.team?.effective_logo_url || home.team?.logo_url} score={st.score.home} bat={!isFinal && battingSide === "home"} />
            <div>
              <div className="font-data text-2xl" data-testid="scoreboard-inning">{isFinal ? "F" : (st.half === "top" ? "▲" : "▼")} {isFinal ? "" : st.inning}</div>
              {!isFinal && <div className="text-[11px] text-white/50 mt-1"><OutDots n={st.outs} /></div>}
              {isAdvanced && !isFinal && (
                <div className="mt-1 text-[11px] text-white/70" data-testid="scoreboard-count">
                  <span className="font-data text-sm text-white">{count.balls}-{count.strikes}</span> <span className="text-white/40">B-S</span>
                </div>
              )}
              {isAdvanced && !isFinal && sc.current_pitcher && (
                <div className="text-[10px] text-white/40 mt-0.5" data-testid="current-pitcher">P: {sc.current_pitcher.name}</div>
              )}
            </div>
            <ScoreCol name={away.team?.name || "Away"} logo={away.team?.effective_logo_url || away.team?.logo_url} score={st.score.away} bat={!isFinal && battingSide === "away"} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-20">
        <div className="max-w-5xl mx-auto px-2 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setTab(t)}
              className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 ${tab === t ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400"}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {tab === "Live" && (
          <div className="space-y-6">
            <Linescore sc={sc} />
            {canManage && !isFinal
              ? <ScorePad game={game} sc={sc} roster={roster} isOurBat={isOurBat} battingSide={battingSide} isAdvanced={isAdvanced} count={count} onDone={load} />
              : !isFinal && <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500 text-center">Live scoring in progress. Sign in as a team manager to score.</div>}
            {isAdvanced && <ArmSafety arm={sc.arm_safety} currentPitcherId={sc.current_pitcher?.id} />}
            <RecentPlays pbp={sc.play_by_play.slice(0, 6)} />
          </div>
        )}
        {tab === "Box Score" && <BoxScore sc={sc} />}
        {tab === "Play-by-Play" && <PlayByPlay pbp={sc.play_by_play} />}
        {tab === "Statistics" && <TeamStats sc={sc} />}
      </div>
    </div>
  );
}

function ScorePad({ game, sc, roster, isOurBat, battingSide, isAdvanced, count, onDone }) {
  const [batter, setBatter] = useState("");
  const [outcome, setOutcome] = useState(null);
  const [rbi, setRbi] = useState(0);
  const [runs, setRuns] = useState(0);
  const [earned, setEarned] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const alertedRef = useRef({});

  const pick = (o) => { setOutcome(o); setRuns(o === "home_run" ? 1 : 0); setRbi(o === "home_run" ? 1 : 0); };

  const post = (body) => {
    setBusy(true);
    api.post(`/live/games/${game.id}/score`, body)
      .then(() => { setOutcome(null); setRbi(0); setRuns(0); setEarned(true); onDone(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(false));
  };
  const record = () => {
    if (!outcome) return toast.error("Pick an outcome");
    if (isOurBat && !batter) return toast.error("Select the batter");
    post({ outcome, batter_id: isOurBat ? batter : null, rbi, runs, earned });
  };
  const pitch = (pr) => {
    api.post(`/live/games/${game.id}/pitch`, { pitch_result: pr, batter_id: isOurBat ? batter : null })
      .then(({ data }) => {
        if (data.resolved) toast.success(data.resolved === "walk" ? "Walk" : "Strikeout");
        if (data.awaiting_outcome) toast.info("Ball in play — pick the outcome");
        const a = data.arm_safety;
        if (a && a.level !== "ok") {
          const last = alertedRef.current[a.player_id];
          if (last !== a.level) {
            alertedRef.current[a.player_id] = a.level;
            if (a.level === "critical") toast.error(`Arm safety: pitcher has hit the ${a.daily_max}-pitch daily limit. Time to make a change.`, { icon: "🛑", duration: 8000 });
            else toast.warning(`Arm safety: pitcher approaching the limit (${a.pitches}/${a.daily_max}, ${a.remaining} to go).`, { icon: "⚠️", duration: 6000 });
          }
        }
        onDone();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  };
  const undo = () => api.post(`/live/games/${game.id}/score/undo`).then(onDone).catch(() => toast.error("Undo failed"));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="score-pad">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline flex items-center gap-2"><Radio className="w-4 h-4 text-[#E63946]" /> {battingSide === "home" ? "Home" : "Away"} Batting {isOurBat ? "(Your Team)" : "(Opponent)"}</div>
        <button data-testid="undo-btn" onClick={undo} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#E63946]"><Undo2 className="w-3.5 h-3.5" /> Undo</button>
      </div>

      {isOurBat && (
        <select data-testid="batter-select" value={batter} onChange={(e) => setBatter(e.target.value)}
          className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mb-3">
          <option value="">Select batter…</option>
          {roster.map((p) => <option key={p.player_id} value={p.player_id}>{p.number ? `#${p.number} ` : ""}{p.first_name} {p.last_name}</option>)}
        </select>
      )}

      {isAdvanced && (
        <div className="mb-3 p-3 bg-[#0A192F]/5 rounded-sm" data-testid="pitch-pad">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Target className="w-3.5 h-3.5 text-[#E63946]" /> Count <span className="font-data text-sm text-[#0A192F]" data-testid="count-display">{count.balls}-{count.strikes}</span></div>
            <button data-testid="change-pitcher-btn" onClick={() => setShowSub((s) => !s)} className="text-xs font-semibold text-[#E63946] flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> Change Pitcher</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PITCHES.map(([pr, lbl]) => (
              <button key={pr} data-testid={`pitch-${pr}`} onClick={() => pitch(pr)}
                className="px-3 py-2 rounded-sm text-xs font-bold border bg-white border-slate-200 text-[#0A192F] hover:border-[#E63946]">{lbl}</button>
            ))}
          </div>
          {showSub && <PitcherChange game={game} sc={sc} roster={roster} onDone={() => { setShowSub(false); onDone(); }} />}
        </div>
      )}

      {GROUPS.map((g) => (
        <div key={g.title} className="mb-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{g.title}</div>
          <div className="flex flex-wrap gap-1.5">
            {g.items.map(([o, short]) => (
              <button key={o} data-testid={`outcome-${o}`} onClick={() => pick(o)}
                className={`px-3 py-2 rounded-sm text-xs font-bold border transition-colors ${outcome === o ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-slate-50 border-slate-200 text-[#0A192F] hover:border-[#E63946]"}`}>{short}</button>
            ))}
          </div>
        </div>
      ))}

      {outcome && (
        <div className="flex items-center gap-4 mt-3 p-3 bg-slate-50 rounded-sm flex-wrap" data-testid="play-confirm">
          <span className="text-sm font-semibold flex-1">{LABELS[outcome]}</span>
          <Stepper label="RBI" v={rbi} set={setRbi} />
          <Stepper label="Runs" v={runs} set={setRuns} />
          {isAdvanced && !isOurBat && runs > 0 && (
            <label className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="earned-toggle">
              <input type="checkbox" checked={earned} onChange={(e) => setEarned(e.target.checked)} /> Earned
            </label>
          )}
          <button data-testid="record-play-btn" disabled={busy} onClick={record}
            className="bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-5 py-2 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50">Record</button>
        </div>
      )}

      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
        <button data-testid="quick-run-btn" disabled={busy} onClick={() => post({ outcome: "run", runs: 1 })}
          className="flex-1 py-2 bg-emerald-50 text-emerald-700 text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-emerald-100">+ Run</button>
        <button data-testid="quick-out-btn" disabled={busy} onClick={() => post({ outcome: "out" })}
          className="flex-1 py-2 bg-slate-100 text-slate-600 text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-200">+ Out</button>
      </div>
    </div>
  );
}

function PitcherChange({ game, sc, roster, onDone }) {
  const [pid, setPid] = useState("");
  const [avail, setAvail] = useState({});
  useEffect(() => {
    api.get(`/live/teams/${game.team_id}/pitcher-availability`)
      .then(({ data }) => setAvail(data.availability || {})).catch(() => {});
  }, [game.team_id]);
  const submit = () => {
    if (!pid) return toast.error("Select the new pitcher");
    const a = avail[pid];
    if (a && a.available === false && !window.confirm(`This pitcher threw ${a.last_pitches} pitches on ${a.last_date} and needs rest until ${a.eligible_date} (${a.days_until_eligible} day(s) left). Bring them in anyway?`)) return;
    api.post(`/live/games/${game.id}/substitution`, { sub_type: "pitching_change", player_in: pid, player_out: sc.current_pitcher?.id, side: sc.our_side })
      .then(() => { toast.success("Pitching change recorded"); onDone(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  };
  return (
    <div className="mt-3 flex items-center gap-2" data-testid="pitcher-change-form">
      <select data-testid="new-pitcher-select" value={pid} onChange={(e) => setPid(e.target.value)} className="flex-1 border border-slate-200 rounded-sm px-2 py-1.5 text-sm">
        <option value="">New pitcher…</option>
        {roster.map((p) => {
          const a = avail[p.player_id];
          const tag = a && a.available === false ? `  ⛔ resting until ${a.eligible_date}` : "";
          return <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}{tag}</option>;
        })}
      </select>
      <button data-testid="confirm-pitcher-change-btn" onClick={submit} className="bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-sm">Confirm</button>
    </div>
  );
}

function ArmSafety({ arm, currentPitcherId }) {
  if (!arm || !arm.enabled || !(arm.pitchers || []).length) return null;
  const barColor = (lvl) => (lvl === "critical" ? "bg-[#E63946]" : lvl === "warning" ? "bg-amber-500" : "bg-emerald-500");
  const chip = (lvl) => (lvl === "critical" ? "bg-[#E63946] text-white" : lvl === "warning" ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700");
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="arm-safety-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#E63946]" /> Arm Safety</div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{arm.rule_label || arm.division} · Limit {arm.daily_max}</span>
      </div>
      <div className="space-y-3">
        {arm.pitchers.map((p) => {
          const pct = Math.min(100, Math.round((p.pitches / arm.daily_max) * 100));
          return (
            <div key={p.player_id} data-testid={`arm-safety-${p.player_id}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-semibold text-[#0A192F] flex items-center gap-1.5">
                  {p.player_id === currentPitcherId && <span className="w-1.5 h-1.5 rounded-full bg-[#E63946] animate-pulse" />}
                  {p.name}
                </span>
                <span className="flex items-center gap-2">
                  <span className="font-data text-[#0A192F]">{p.pitches}<span className="text-slate-400">/{arm.daily_max}</span></span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${chip(p.level)}`}>
                    {p.level === "critical" ? "Limit" : p.level === "warning" ? "Watch" : "OK"}
                  </span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${barColor(p.level)} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">{p.remaining} pitches remaining · needs {p.rest_days} day(s) rest after this outing</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stepper({ label, v, set }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="flex items-center gap-1 mt-0.5">
        <button onClick={() => set(Math.max(0, v - 1))} className="w-6 h-6 rounded-sm bg-white border border-slate-200 grid place-items-center"><Minus className="w-3 h-3" /></button>
        <span className="font-data text-lg w-6" data-testid={`stepper-${label.toLowerCase()}`}>{v}</span>
        <button onClick={() => set(Math.min(4, v + 1))} className="w-6 h-6 rounded-sm bg-white border border-slate-200 grid place-items-center"><Plus className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

function Linescore({ sc }) {
  const innings = sc.linescore.length ? sc.linescore.map((l) => l.inning) : [1];
  const row = (side) => innings.map((i) => { const l = sc.linescore.find((x) => x.inning === i); return l ? l[side] : 0; });
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="linescore">
      <table className="w-full text-center text-sm min-w-[360px]">
        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <th className="text-left px-3 py-2">Team</th>
          {innings.map((i) => <th key={i} className="px-2 py-2 w-7">{i}</th>)}
          <th className="px-2 py-2 font-bold text-[#0A192F]">R</th><th className="px-2 py-2">H</th>
        </tr></thead>
        <tbody>
          {["away", "home"].map((side) => (
            <tr key={side} className="border-b border-slate-50 last:border-0">
              <td className="text-left px-3 py-2 font-semibold truncate max-w-[120px]">{sc.box_score[side].team?.name || side}</td>
              {row(side).map((r, i) => <td key={i} className="px-2 py-2 font-data text-slate-600">{r}</td>)}
              <td className="px-2 py-2 font-data font-bold text-[#0A192F]">{sc.state.score[side]}</td>
              <td className="px-2 py-2 font-data text-slate-500">{sc.box_score[side].hits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentPlays({ pbp }) {
  if (!pbp.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="label-overline mb-2">Recent Plays</div>
      <ul className="space-y-1.5" data-testid="recent-plays">
        {pbp.map((p, i) => <li key={i} className="text-sm text-slate-600 border-l-2 border-[#E63946] pl-2">{p.description}</li>)}
      </ul>
    </div>
  );
}

function BoxScore({ sc }) {
  return (
    <div className="space-y-6" data-testid="box-score">
      <Linescore sc={sc} />
      {["away", "home"].map((side) => {
        const b = sc.box_score[side];
        return (
          <div key={side} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-[#0A192F] text-white text-sm font-display uppercase tracking-tight">{b.team?.name || side} — Batting</div>
            {b.batting.length === 0 ? <div className="p-4 text-sm text-slate-400">No batting lines recorded.</div> : (
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[420px]">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  {["Player", "AB", "H", "2B", "3B", "HR", "RBI", "BB", "K", "AVG"].map((h) => <th key={h} className={`py-2 px-2 ${h === "Player" ? "text-left" : ""}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {b.batting.map((p) => (
                    <tr key={p.player_id} className="border-b border-slate-50 last:border-0">
                      <td className="text-left px-2 py-2 font-semibold">{p.first_name} {p.last_name}</td>
                      {[p.ab, p.h, p.doubles, p.triples, p.hr, p.rbi, p.bb, p.so].map((v, i) => <td key={i} className="px-2 py-2 text-center font-data text-slate-600">{v}</td>)}
                      <td className="px-2 py-2 text-center font-data text-[#0A192F]">{p.avg != null ? p.avg.toFixed(3).replace(/^0/, "") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
            {b.pitching && b.pitching.length > 0 && (
              <div data-testid={`pitching-box-${side}`}>
                <div className="px-4 py-2 bg-[#0A192F]/90 text-white text-xs font-display uppercase tracking-tight border-t border-white/10">Pitching</div>
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[460px]">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    {["Pitcher", "IP", "H", "R", "ER", "BB", "K", "HR", "P", "S%", "ERA", "WHIP"].map((h) => <th key={h} className={`py-2 px-2 ${h === "Pitcher" ? "text-left" : ""}`}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {b.pitching.map((p) => (
                      <tr key={p.player_id} className="border-b border-slate-50 last:border-0">
                        <td className="text-left px-2 py-2 font-semibold">{p.first_name} {p.last_name}</td>
                        <td className="px-2 py-2 text-center font-data text-[#0A192F]">{p.ip}</td>
                        {[p.h, p.r, p.er, p.bb, p.so, p.hr, p.pitches].map((v, i) => <td key={i} className="px-2 py-2 text-center font-data text-slate-600">{v}</td>)}
                        <td className="px-2 py-2 text-center font-data text-slate-600">{p.strike_pct != null ? `${Math.round(p.strike_pct * 100)}%` : "—"}</td>
                        <td className="px-2 py-2 text-center font-data text-[#0A192F]">{p.era != null ? p.era.toFixed(2) : "—"}</td>
                        <td className="px-2 py-2 text-center font-data text-[#0A192F]">{p.whip != null ? p.whip.toFixed(2) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PlayByPlay({ pbp }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="play-by-play">
      <div className="label-overline flex items-center gap-2 mb-3"><ListOrdered className="w-4 h-4 text-[#E63946]" /> Play-by-Play</div>
      {pbp.length === 0 ? <div className="text-sm text-slate-400 py-4">No plays recorded yet.</div> : (
        <ul className="space-y-2">
          {pbp.map((p, i) => (
            <li key={i} className={`flex items-start gap-3 text-sm border-b border-slate-50 last:border-0 pb-2 ${p.type === "sub" ? "text-[#E63946]" : ""}`}>
              <span className="text-[10px] font-bold text-slate-400 uppercase w-14 shrink-0 mt-0.5">{p.half === "top" ? "TOP" : "BOT"} {p.inning}</span>
              {p.type === "sub"
                ? <span className="font-semibold flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> {p.description}</span>
                : <span className="text-slate-700">{p.count ? `[${p.count}] ` : ""}{p.label}{p.rbi ? ` · ${p.rbi} RBI` : ""}{p.runs ? ` · ${p.runs} R` : ""}{p.batter_name ? ` — ${p.batter_name}` : ""}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TeamStats({ sc }) {
  const both = ["away", "home"].map((s) => sc.box_score[s]);
  return (
    <div className="space-y-4" data-testid="team-stats-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        {both.map((b, i) => {
          const tot = b.batting.reduce((a, p) => ({ ab: a.ab + p.ab, h: a.h + p.h, hr: a.hr + p.hr, rbi: a.rbi + p.rbi, bb: a.bb + p.bb, so: a.so + p.so }), { ab: 0, h: 0, hr: 0, rbi: 0, bb: 0, so: 0 });
          const p = (b.pitching || []).reduce((a, x) => ({ outs: a.outs + x.ip_outs, er: a.er + x.er, h: a.h + x.h, bb: a.bb + x.bb, so: a.so + x.so, pitches: a.pitches + x.pitches, strikes: a.strikes + x.strikes }), { outs: 0, er: 0, h: 0, bb: 0, so: 0, pitches: 0, strikes: 0 });
          const ip = p.outs / 3;
          const era = ip ? (p.er * 9 / ip).toFixed(2) : null;
          const whip = ip ? ((p.bb + p.h) / ip).toFixed(2) : null;
          const spct = p.pitches ? Math.round(p.strikes / p.pitches * 100) : null;
          return (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="label-overline flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-[#E63946]" />{b.team?.name || "Team"}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Batting</div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["Runs", b.runs], ["Hits", b.hits], ["Errors", b.errors], ["HR", tot.hr], ["RBI", tot.rbi], ["BB", tot.bb], ["K", tot.so]].map(([k, v]) => (
                  <div key={k}><div className="font-data text-2xl text-[#0A192F]">{v}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div></div>
                ))}
              </div>
              {(b.pitching || []).length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Pitching</div>
                  <div className="grid grid-cols-3 gap-3">
                    {[["IP", `${Math.floor(p.outs / 3)}.${p.outs % 3}`], ["ERA", era ?? "—"], ["WHIP", whip ?? "—"], ["K", p.so], ["Pitches", p.pitches], ["Strike %", spct != null ? `${spct}%` : "—"]].map(([k, v]) => (
                      <div key={k}><div className="font-data text-2xl text-[#0A192F]">{v}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div></div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      {sc.substitutions && sc.substitutions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="substitutions-log">
          <div className="label-overline flex items-center gap-2 mb-2"><Repeat className="w-4 h-4 text-[#E63946]" /> Substitutions</div>
          <ul className="space-y-1.5">
            {sc.substitutions.map((s, i) => <li key={i} className="text-sm text-slate-600">{s.description}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

const ScoreCol = ({ name, logo, score, bat }) => (
  <div>
    <div className="flex items-center justify-center gap-1.5 mb-1">
      <EntityLogo src={logo} alt={name} size="w-6 h-6" textSize="text-[7px]" variant="dark" />
      <div className="text-xs text-white/60 truncate flex items-center gap-1">{bat && <span className="w-1.5 h-1.5 rounded-full bg-[#E63946] animate-pulse" />}{name}</div>
    </div>
    <div className="font-data text-5xl">{score}</div>
  </div>
);
const OutDots = ({ n }) => (
  <span className="inline-flex items-center gap-1">{[0, 1, 2].map((i) => <span key={i} className={`w-2 h-2 rounded-full ${i < n ? "bg-[#E63946]" : "bg-white/20"}`} />)}<span className="ml-1">{n} OUT</span></span>
);
